'use client';

import { useState } from 'react';
import Link from 'next/link';
import { useCommandCenterTool } from '@/lib/command-center/useCommandCenterTool';

type View = 'landing' | 'assessment' | 'results';

interface Question {
  id: string;
  text: string;
  requirement: string;
  weight: number;
}

interface Section {
  id: string;
  title: string;
  description: string;
  questions: Question[];
}

const sections: Section[] = [
  {
    id: 'network-security',
    title: 'Requirement 1-2: Network Security',
    description: 'Install and maintain network security controls and apply secure configurations',
    questions: [
      { id: 'ns1', text: 'Do you have firewalls installed and configured to protect cardholder data?', requirement: '1.1', weight: 10 },
      { id: 'ns2', text: 'Are firewall and router configurations reviewed at least every six months?', requirement: '1.1.7', weight: 8 },
      { id: 'ns3', text: 'Is a network diagram maintained that shows all cardholder data flows?', requirement: '1.2.4', weight: 7 },
      { id: 'ns4', text: 'Are vendor-supplied defaults for system passwords changed before installation?', requirement: '2.1', weight: 10 },
      { id: 'ns5', text: 'Are unnecessary default accounts disabled or removed?', requirement: '2.1', weight: 8 },
      { id: 'ns6', text: 'Is there a configuration standard for all system components?', requirement: '2.2', weight: 8 },
    ],
  },
  {
    id: 'protect-data',
    title: 'Requirement 3-4: Protect Cardholder Data',
    description: 'Protect stored account data and protect cardholder data with strong cryptography',
    questions: [
      { id: 'pd1', text: 'Do you have a data retention policy that limits storage of cardholder data?', requirement: '3.1', weight: 9 },
      { id: 'pd2', text: 'Is the full contents of any track never stored after authorization?', requirement: '3.2', weight: 10 },
      { id: 'pd3', text: 'Is the card verification code/value (CVV) never stored after authorization?', requirement: '3.2.2', weight: 10 },
      { id: 'pd4', text: 'Is the PIN or encrypted PIN block never stored after authorization?', requirement: '3.2.3', weight: 10 },
      { id: 'pd5', text: 'Is stored PAN rendered unreadable (encryption, hashing, truncation)?', requirement: '3.5', weight: 10 },
      { id: 'pd6', text: 'Is strong cryptography used to transmit cardholder data across open networks?', requirement: '4.1', weight: 10 },
      { id: 'pd7', text: 'Are unencrypted PANs never sent via end-user messaging technologies?', requirement: '4.2', weight: 9 },
    ],
  },
  {
    id: 'vulnerability-mgmt',
    title: 'Requirement 5-6: Vulnerability Management',
    description: 'Protect systems against malware and develop secure systems and software',
    questions: [
      { id: 'vm1', text: 'Is anti-malware software deployed on all systems commonly affected by malware?', requirement: '5.1', weight: 9 },
      { id: 'vm2', text: 'Is anti-malware software kept current and actively running?', requirement: '5.2', weight: 9 },
      { id: 'vm3', text: 'Are periodic evaluations performed to identify new malware threats?', requirement: '5.2', weight: 7 },
      { id: 'vm4', text: 'Is there a process to identify and rank security vulnerabilities?', requirement: '6.1', weight: 8 },
      { id: 'vm5', text: 'Are critical security patches installed within one month of release?', requirement: '6.2', weight: 9 },
      { id: 'vm6', text: 'Are software development processes based on industry standards and best practices?', requirement: '6.3', weight: 8 },
      { id: 'vm7', text: 'Are code changes reviewed by someone other than the author before production?', requirement: '6.3.2', weight: 8 },
      { id: 'vm8', text: 'Are web applications protected against known attacks (OWASP Top 10)?', requirement: '6.4', weight: 9 },
    ],
  },
  {
    id: 'access-control',
    title: 'Requirement 7-8: Access Control',
    description: 'Restrict access to cardholder data and identify users',
    questions: [
      { id: 'ac1', text: 'Is access to cardholder data restricted to personnel with business need?', requirement: '7.1', weight: 10 },
      { id: 'ac2', text: 'Is there an access control system that restricts access based on job function?', requirement: '7.2', weight: 9 },
      { id: 'ac3', text: 'Is "deny all" the default setting for access control systems?', requirement: '7.2', weight: 8 },
      { id: 'ac4', text: 'Is a unique ID assigned to each person with computer access?', requirement: '8.1', weight: 10 },
      { id: 'ac5', text: 'Is user access immediately revoked for terminated users?', requirement: '8.1.3', weight: 9 },
      { id: 'ac6', text: 'Are inactive user accounts removed or disabled within 90 days?', requirement: '8.1.4', weight: 8 },
      { id: 'ac7', text: 'Is multi-factor authentication used for all remote network access?', requirement: '8.4', weight: 10 },
      { id: 'ac8', text: 'Are passwords at least 12 characters (or 8 with complexity)?', requirement: '8.3.6', weight: 8 },
    ],
  },
  {
    id: 'physical-security',
    title: 'Requirement 9: Physical Security',
    description: 'Restrict physical access to cardholder data',
    questions: [
      { id: 'ps1', text: 'Are physical access controls in place for systems storing cardholder data?', requirement: '9.1', weight: 9 },
      { id: 'ps2', text: 'Are video cameras or access control mechanisms used to monitor sensitive areas?', requirement: '9.1.1', weight: 8 },
      { id: 'ps3', text: 'Is visitor access authorized, logged, and visitors escorted?', requirement: '9.3', weight: 7 },
      { id: 'ps4', text: 'Is media containing cardholder data physically secured?', requirement: '9.5', weight: 8 },
      { id: 'ps5', text: 'Is media destroyed when no longer needed for business or legal reasons?', requirement: '9.8', weight: 8 },
      { id: 'ps6', text: 'Are point-of-interaction devices protected from tampering?', requirement: '9.9', weight: 9 },
    ],
  },
  {
    id: 'monitoring',
    title: 'Requirement 10-11: Monitoring & Testing',
    description: 'Log and monitor access and regularly test security systems',
    questions: [
      { id: 'mt1', text: 'Are audit logs enabled and active for all system components?', requirement: '10.1', weight: 9 },
      { id: 'mt2', text: 'Do audit logs capture all required events (user access, actions, etc.)?', requirement: '10.2', weight: 9 },
      { id: 'mt3', text: 'Are audit logs protected from unauthorized modifications?', requirement: '10.3', weight: 9 },
      { id: 'mt4', text: 'Are logs reviewed daily for security events?', requirement: '10.4', weight: 8 },
      { id: 'mt5', text: 'Are audit logs retained for at least one year (3 months immediately available)?', requirement: '10.5', weight: 8 },
      { id: 'mt6', text: 'Are wireless access points tested quarterly?', requirement: '11.1', weight: 7 },
      { id: 'mt7', text: 'Are vulnerability scans run at least quarterly and after significant changes?', requirement: '11.2', weight: 9 },
      { id: 'mt8', text: 'Are penetration tests performed at least annually?', requirement: '11.3', weight: 9 },
      { id: 'mt9', text: 'Is intrusion detection/prevention used to monitor network traffic?', requirement: '11.4', weight: 8 },
      { id: 'mt10', text: 'Is file integrity monitoring deployed on critical system files?', requirement: '11.5', weight: 8 },
    ],
  },
  {
    id: 'policies',
    title: 'Requirement 12: Security Policies',
    description: 'Support information security with organizational policies and programs',
    questions: [
      { id: 'sp1', text: 'Is there a documented information security policy reviewed annually?', requirement: '12.1', weight: 9 },
      { id: 'sp2', text: 'Is a formal risk assessment performed at least annually?', requirement: '12.2', weight: 9 },
      { id: 'sp3', text: 'Are security policies and procedures clearly defined for all personnel?', requirement: '12.3', weight: 8 },
      { id: 'sp4', text: 'Is security awareness training provided upon hire and annually thereafter?', requirement: '12.6', weight: 8 },
      { id: 'sp5', text: 'Are personnel screened prior to hire (background checks)?', requirement: '12.7', weight: 7 },
      { id: 'sp6', text: 'Is there an incident response plan that is tested annually?', requirement: '12.10', weight: 9 },
      { id: 'sp7', text: 'Are service provider relationships managed with appropriate agreements?', requirement: '12.8', weight: 8 },
      { id: 'sp8', text: 'Is service provider PCI DSS compliance status monitored at least annually?', requirement: '12.8.4', weight: 8 },
    ],
  },
];

const totalWeight = sections.reduce(
  (sum, section) => sum + section.questions.reduce((qSum, q) => qSum + q.weight, 0),
  0
);

export default function PCIDSSChecker() {
  const [view, setView] = useState<View>('landing');
  const [currentSection, setCurrentSection] = useState(0);
  const [answers, setAnswers] = useState<Record<string, 'yes' | 'no' | 'partial' | 'na'>>({});
  const [merchantLevel, setMerchantLevel] = useState<'1' | '2' | '3' | '4' | ''>('');
  const [saqType, setSaqType] = useState<string>('');
  const [saved, setSaved] = useState(false);

  const {
    enabled: commandCenterEnabled,
    saving,
    saveResult,
  } = useCommandCenterTool({
    toolId: 'pci-dss-checker',
    toolCategory: 'compliance',
  });

  const handleSaveToCommandCenter = async () => {
    const { score, gaps } = calculateScore();
    const sectionScores: Record<string, number> = {};
    sections.forEach((section) => {
      sectionScores[section.id] = getSectionScore(section.id);
    });

    const result = await saveResult({
      name: 'PCI DSS Compliance Assessment',
      score_overall: score,
      scores: sectionScores,
      answers,
      gaps: gaps.map((g, i) => ({
        id: `gap-${i}`,
        title: g.question,
        severity: g.status === 'Not Met' ? 'high' : 'medium',
        description: `Requirement: ${g.requirement}`,
      })),
      recommendations: score < 95 ? [
        { id: 'rec-1', title: 'Address all Not Met requirements', priority: 'high' },
        { id: 'rec-2', title: 'Complete partial implementations', priority: 'high' },
        { id: 'rec-3', title: 'Document security policies and procedures', priority: 'medium' },
        { id: 'rec-4', title: 'Schedule quarterly vulnerability scans', priority: 'medium' },
        { id: 'rec-5', title: 'Plan annual penetration testing', priority: 'medium' },
      ] : [],
      metadata: { merchantLevel, saqType },
    });

    if (result) setSaved(true);
  };

  const handleAnswer = (questionId: string, value: 'yes' | 'no' | 'partial' | 'na') => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  const calculateScore = () => {
    let earned = 0;
    let possible = 0;
    const gaps: { requirement: string; question: string; status: string }[] = [];

    sections.forEach((section) => {
      section.questions.forEach((q) => {
        const answer = answers[q.id];
        if (answer === 'na') return;

        possible += q.weight;
        if (answer === 'yes') {
          earned += q.weight;
        } else if (answer === 'partial') {
          earned += q.weight * 0.5;
          gaps.push({ requirement: q.requirement, question: q.text, status: 'Partial' });
        } else if (answer === 'no') {
          gaps.push({ requirement: q.requirement, question: q.text, status: 'Not Met' });
        }
      });
    });

    return {
      score: possible > 0 ? Math.round((earned / possible) * 100) : 0,
      gaps: gaps.sort((a, b) => a.requirement.localeCompare(b.requirement)),
    };
  };

  const getSectionScore = (sectionId: string) => {
    const section = sections.find((s) => s.id === sectionId);
    if (!section) return 0;

    let earned = 0;
    let possible = 0;

    section.questions.forEach((q) => {
      const answer = answers[q.id];
      if (answer === 'na') return;
      possible += q.weight;
      if (answer === 'yes') earned += q.weight;
      else if (answer === 'partial') earned += q.weight * 0.5;
    });

    return possible > 0 ? Math.round((earned / possible) * 100) : 0;
  };

  const getComplianceLevel = (score: number) => {
    if (score >= 95) return { level: 'Compliant', color: 'text-green-400', bg: 'bg-green-500/20' };
    if (score >= 80) return { level: 'Near Compliant', color: 'text-yellow-400', bg: 'bg-yellow-500/20' };
    if (score >= 60) return { level: 'Partially Compliant', color: 'text-accent-400', bg: 'bg-accent-500/20' };
    return { level: 'Not Compliant', color: 'text-red-400', bg: 'bg-red-500/20' };
  };

  const exportToMarkdown = () => {
    const { score, gaps } = calculateScore();
    const compliance = getComplianceLevel(score);

    let md = `# PCI DSS Compliance Assessment Report\n\n`;
    md += `**Generated:** ${new Date().toLocaleDateString()}\n\n`;
    md += `**Merchant Level:** ${merchantLevel || 'Not specified'}\n`;
    md += `**SAQ Type:** ${saqType || 'Not specified'}\n\n`;
    md += `## Overall Compliance Score\n\n`;
    md += `**Score:** ${score}%\n`;
    md += `**Status:** ${compliance.level}\n\n`;

    md += `## Section Scores\n\n`;
    md += `| Section | Score |\n`;
    md += `|---------|-------|\n`;
    sections.forEach((section) => {
      md += `| ${section.title} | ${getSectionScore(section.id)}% |\n`;
    });

    if (gaps.length > 0) {
      md += `\n## Compliance Gaps\n\n`;
      md += `| Requirement | Issue | Status |\n`;
      md += `|-------------|-------|--------|\n`;
      gaps.forEach((gap) => {
        md += `| ${gap.requirement} | ${gap.question} | ${gap.status} |\n`;
      });
    }

    md += `\n## PCI DSS Validation Requirements by Merchant Level\n\n`;
    md += `| Level | Transactions/Year | Validation Requirements |\n`;
    md += `|-------|-------------------|-------------------------|\n`;
    md += `| 1 | >6 million | Annual on-site assessment by QSA, quarterly ASV scan |\n`;
    md += `| 2 | 1-6 million | Annual SAQ, quarterly ASV scan |\n`;
    md += `| 3 | 20K-1 million e-commerce | Annual SAQ, quarterly ASV scan |\n`;
    md += `| 4 | <20K e-commerce or <1M other | Annual SAQ, quarterly ASV scan (recommended) |\n`;

    md += `\n## Recommended Next Steps\n\n`;
    if (score < 95) {
      md += `1. Address all "Not Met" requirements as priority\n`;
      md += `2. Complete partial implementations to full compliance\n`;
      md += `3. Document all security policies and procedures\n`;
      md += `4. Schedule quarterly vulnerability scans with ASV\n`;
      md += `5. Plan annual penetration testing\n`;
      md += `6. Engage QSA for formal assessment (Level 1 merchants)\n`;
    } else {
      md += `1. Maintain current security controls\n`;
      md += `2. Continue quarterly ASV scans\n`;
      md += `3. Complete annual SAQ or QSA assessment\n`;
      md += `4. Monitor for PCI DSS 4.0 future-dated requirements\n`;
    }

    md += `\n---\n*Assessment conducted using The Art of CTO PCI DSS Checker*\n`;

    const blob = new Blob([md], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pci-dss-assessment-${new Date().toISOString().split('T')[0]}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (view === 'landing') {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-4xl mx-auto px-4 py-12">
          <Link href="/tools" className="text-foreground-tertiary hover:text-accent mb-8 inline-block">
            ← Back to Tools
          </Link>

          <div className="text-center mb-12">
            <span className="text-accent text-sm font-medium tracking-wider uppercase">
              Payment Security
            </span>
            <h1 className="text-4xl font-bold text-foreground mt-2 mb-4">
              PCI DSS Compliance Checker
            </h1>
            <p className="text-foreground-secondary text-lg max-w-2xl mx-auto">
              Assess your compliance with the Payment Card Industry Data Security Standard (PCI DSS 4.0).
              Identify gaps and create a roadmap to protect cardholder data.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <div className="bg-surface border border-border-muted rounded-lg p-6">
              <h3 className="text-foreground font-semibold mb-3">What is PCI DSS?</h3>
              <p className="text-foreground-secondary text-sm">
                The Payment Card Industry Data Security Standard is a set of security requirements
                for organizations that handle credit card data. Compliance is mandatory for all
                businesses that process, store, or transmit cardholder data.
              </p>
            </div>
            <div className="bg-surface border border-border-muted rounded-lg p-6">
              <h3 className="text-foreground font-semibold mb-3">PCI DSS 4.0</h3>
              <p className="text-foreground-secondary text-sm">
                Version 4.0 was released in March 2022 with a transition period. All organizations
                must comply with PCI DSS 4.0 by March 31, 2025. Future-dated requirements are
                best practices until March 31, 2025.
              </p>
            </div>
          </div>

          <div className="bg-surface border border-border-muted rounded-lg p-6 mb-8">
            <h3 className="text-foreground font-semibold mb-4">Before You Begin</h3>

            <div className="mb-4">
              <label className="text-foreground-secondary text-sm block mb-2">
                What is your merchant level? (Based on annual transaction volume)
              </label>
              <select
                value={merchantLevel}
                onChange={(e) => setMerchantLevel(e.target.value as typeof merchantLevel)}
                className="w-full bg-background border border-border rounded-lg px-4 py-2 text-foreground"
              >
                <option value="">Select merchant level...</option>
                <option value="1">Level 1: Over 6 million transactions/year</option>
                <option value="2">Level 2: 1 to 6 million transactions/year</option>
                <option value="3">Level 3: 20,000 to 1 million e-commerce transactions/year</option>
                <option value="4">Level 4: Less than 20,000 e-commerce or less than 1 million other transactions/year</option>
              </select>
            </div>

            <div>
              <label className="text-foreground-secondary text-sm block mb-2">
                Which SAQ type applies to your organization?
              </label>
              <select
                value={saqType}
                onChange={(e) => setSaqType(e.target.value)}
                className="w-full bg-background border border-border rounded-lg px-4 py-2 text-foreground"
              >
                <option value="">Select SAQ type...</option>
                <option value="SAQ A">SAQ A: Card-not-present, all cardholder data functions outsourced</option>
                <option value="SAQ A-EP">SAQ A-EP: E-commerce with partially outsourced payment page</option>
                <option value="SAQ B">SAQ B: Imprint or standalone dial-out terminals only</option>
                <option value="SAQ B-IP">SAQ B-IP: Standalone IP-connected PTS terminals</option>
                <option value="SAQ C">SAQ C: Payment application connected to internet</option>
                <option value="SAQ C-VT">SAQ C-VT: Web-based virtual terminals</option>
                <option value="SAQ P2PE">SAQ P2PE: Hardware payment terminals using P2PE</option>
                <option value="SAQ D">SAQ D: All other merchants and service providers</option>
                <option value="ROC">ROC: Report on Compliance (Level 1 merchants)</option>
              </select>
            </div>
          </div>

          <div className="bg-surface border border-border-muted rounded-lg p-6 mb-8">
            <h3 className="text-foreground font-semibold mb-3">12 PCI DSS Requirements</h3>
            <div className="grid md:grid-cols-2 gap-4 text-sm text-foreground-secondary">
              <div>
                <p className="mb-2"><span className="text-accent">1.</span> Install and maintain network security controls</p>
                <p className="mb-2"><span className="text-accent">2.</span> Apply secure configurations to all components</p>
                <p className="mb-2"><span className="text-accent">3.</span> Protect stored account data</p>
                <p className="mb-2"><span className="text-accent">4.</span> Protect cardholder data with strong cryptography</p>
                <p className="mb-2"><span className="text-accent">5.</span> Protect systems from malicious software</p>
                <p className="mb-2"><span className="text-accent">6.</span> Develop and maintain secure systems and software</p>
              </div>
              <div>
                <p className="mb-2"><span className="text-accent">7.</span> Restrict access to cardholder data by business need</p>
                <p className="mb-2"><span className="text-accent">8.</span> Identify users and authenticate access</p>
                <p className="mb-2"><span className="text-accent">9.</span> Restrict physical access to cardholder data</p>
                <p className="mb-2"><span className="text-accent">10.</span> Log and monitor all access to system components</p>
                <p className="mb-2"><span className="text-accent">11.</span> Test security of systems and networks regularly</p>
                <p className="mb-2"><span className="text-accent">12.</span> Support information security with policies and programs</p>
              </div>
            </div>
          </div>

          <div className="text-center">
            <button
              onClick={() => setView('assessment')}
              className="bg-accent hover:bg-accent-hover text-accent-foreground font-semibold px-8 py-3 rounded-lg transition-colors"
            >
              Start Compliance Assessment
            </button>
            <p className="text-foreground-tertiary text-sm mt-4">
              Takes approximately 15-20 minutes to complete
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (view === 'assessment') {
    const section = sections[currentSection];
    const progress = ((currentSection + 1) / sections.length) * 100;

    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-4xl mx-auto px-4 py-12">
          <div className="flex items-center justify-between mb-8">
            <button
              onClick={() => setView('landing')}
              className="text-foreground-tertiary hover:text-accent"
            >
              ← Back
            </button>
            <span className="text-foreground-tertiary text-sm">
              Section {currentSection + 1} of {sections.length}
            </span>
          </div>

          <div className="mb-8">
            <div className="flex justify-between text-sm text-foreground-tertiary mb-2">
              <span>{section.title}</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <div className="h-2 bg-cream/10 rounded-full overflow-hidden">
              <div
                className="h-full bg-accent transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          <div className="bg-surface border border-border-muted rounded-lg p-6 mb-6">
            <h2 className="text-xl font-semibold text-foreground mb-2">{section.title}</h2>
            <p className="text-foreground-tertiary">{section.description}</p>
          </div>

          <div className="space-y-4">
            {section.questions.map((question) => (
              <div
                key={question.id}
                className="bg-surface border border-border-muted rounded-lg p-4"
              >
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div>
                    <span className="text-accent text-xs font-medium">
                      Requirement {question.requirement}
                    </span>
                    <p className="text-foreground mt-1">{question.text}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  {(['yes', 'partial', 'no', 'na'] as const).map((value) => (
                    <button
                      key={value}
                      onClick={() => handleAnswer(question.id, value)}
                      className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                        answers[question.id] === value
                          ? value === 'yes'
                            ? 'bg-green-500/20 text-green-400 border border-green-500/50'
                            : value === 'partial'
                            ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/50'
                            : value === 'no'
                            ? 'bg-red-500/20 text-red-400 border border-red-500/50'
                            : 'bg-gray-500/20 text-gray-400 border border-gray-500/50'
                          : 'bg-cream/5 text-foreground-tertiary border border-border-muted hover:border-border/30'
                      }`}
                    >
                      {value === 'na' ? 'N/A' : value.charAt(0).toUpperCase() + value.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-between mt-8">
            <button
              onClick={() => setCurrentSection(Math.max(0, currentSection - 1))}
              disabled={currentSection === 0}
              className="px-6 py-2 rounded-lg border border-border text-foreground disabled:opacity-50 disabled:cursor-not-allowed hover:border-border/40 transition-colors"
            >
              Previous
            </button>
            {currentSection < sections.length - 1 ? (
              <button
                onClick={() => setCurrentSection(currentSection + 1)}
                className="bg-accent hover:bg-accent-hover text-accent-foreground font-semibold px-6 py-2 rounded-lg transition-colors"
              >
                Next Section
              </button>
            ) : (
              <button
                onClick={() => setView('results')}
                className="bg-accent hover:bg-accent-hover text-accent-foreground font-semibold px-6 py-2 rounded-lg transition-colors"
              >
                View Results
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  const { score, gaps } = calculateScore();
  const compliance = getComplianceLevel(score);

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={() => setView('assessment')}
            className="text-foreground-tertiary hover:text-accent"
          >
            ← Back to Assessment
          </button>
          <button
            onClick={exportToMarkdown}
            className="bg-cream/10 hover:bg-cream/20 text-foreground px-4 py-2 rounded-lg transition-colors"
          >
            Export Report
          </button>
        </div>

        <div className="text-center mb-12">
          <h1 className="text-3xl font-bold text-foreground mb-4">
            PCI DSS Compliance Results
          </h1>
          <div className={`inline-block px-6 py-3 rounded-lg ${compliance.bg}`}>
            <span className={`text-4xl font-bold ${compliance.color}`}>{score}%</span>
            <p className={`${compliance.color} font-medium mt-1`}>{compliance.level}</p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4 mb-8">
          <div className="bg-surface border border-border-muted rounded-lg p-4">
            <h3 className="text-foreground-tertiary text-sm mb-1">Merchant Level</h3>
            <p className="text-foreground font-semibold">
              {merchantLevel ? `Level ${merchantLevel}` : 'Not specified'}
            </p>
          </div>
          <div className="bg-surface border border-border-muted rounded-lg p-4">
            <h3 className="text-foreground-tertiary text-sm mb-1">SAQ Type</h3>
            <p className="text-foreground font-semibold">{saqType || 'Not specified'}</p>
          </div>
        </div>

        <div className="bg-surface border border-border-muted rounded-lg p-6 mb-8">
          <h2 className="text-xl font-semibold text-foreground mb-4">Requirement Scores</h2>
          <div className="space-y-3">
            {sections.map((section) => {
              const sectionScore = getSectionScore(section.id);
              const sectionCompliance = getComplianceLevel(sectionScore);
              return (
                <div key={section.id}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-foreground-secondary">{section.title}</span>
                    <span className={sectionCompliance.color}>{sectionScore}%</span>
                  </div>
                  <div className="h-2 bg-cream/10 rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all ${
                        sectionScore >= 95
                          ? 'bg-green-500'
                          : sectionScore >= 80
                          ? 'bg-yellow-500'
                          : sectionScore >= 60
                          ? 'bg-accent'
                          : 'bg-red-500'
                      }`}
                      style={{ width: `${sectionScore}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {gaps.length > 0 && (
          <div className="bg-surface border border-border-muted rounded-lg p-6 mb-8">
            <h2 className="text-xl font-semibold text-foreground mb-4">
              Compliance Gaps ({gaps.length})
            </h2>
            <div className="space-y-3">
              {gaps.map((gap, index) => (
                <div
                  key={index}
                  className="border border-border-muted rounded-lg p-3"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <span className="text-accent text-xs font-medium">
                        Requirement {gap.requirement}
                      </span>
                      <p className="text-foreground text-sm mt-1">{gap.question}</p>
                    </div>
                    <span
                      className={`text-xs font-medium px-2 py-1 rounded ${
                        gap.status === 'Partial'
                          ? 'bg-yellow-500/20 text-yellow-400'
                          : 'bg-red-500/20 text-red-400'
                      }`}
                    >
                      {gap.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="bg-surface border border-border-muted rounded-lg p-6 mb-8">
          <h2 className="text-xl font-semibold text-foreground mb-4">Validation Requirements</h2>
          <div className="text-foreground-secondary text-sm space-y-3">
            {merchantLevel === '1' ? (
              <>
                <p><strong className="text-foreground">Level 1 Requirements:</strong></p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Annual on-site assessment by a Qualified Security Assessor (QSA)</li>
                  <li>Quarterly network scan by Approved Scanning Vendor (ASV)</li>
                  <li>Attestation of Compliance (AOC) form</li>
                </ul>
              </>
            ) : (
              <>
                <p><strong className="text-foreground">Level {merchantLevel || '2-4'} Requirements:</strong></p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Annual Self-Assessment Questionnaire ({saqType || 'appropriate SAQ type'})</li>
                  <li>Quarterly network scan by Approved Scanning Vendor (ASV)</li>
                  <li>Attestation of Compliance (AOC) form</li>
                </ul>
              </>
            )}
          </div>
        </div>

        <div className="bg-surface border border-border rounded-lg p-6">
          <h2 className="text-xl font-semibold text-foreground mb-4">Key Deadlines</h2>
          <div className="text-foreground-secondary text-sm space-y-2">
            <p><span className="text-accent">March 31, 2024:</span> PCI DSS 3.2.1 retired, 4.0 mandatory</p>
            <p><span className="text-accent">March 31, 2025:</span> Future-dated requirements become mandatory</p>
            <p className="mt-4 text-foreground-tertiary">
              Future-dated requirements include enhanced authentication, expanded encryption,
              and additional security testing requirements.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-4 justify-center mt-8">
          <button
            onClick={exportToMarkdown}
            className="px-6 py-3 bg-cream/10 hover:bg-cream/20 text-foreground rounded-lg transition-colors"
          >
            Export Report
          </button>
          {commandCenterEnabled && (
            <button
              onClick={handleSaveToCommandCenter}
              disabled={saving || saved}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {saving ? 'Saving...' : saved ? 'Saved to Command Center' : 'Save to Command Center'}
            </button>
          )}
          <button
            onClick={() => {
              setView('landing');
              setCurrentSection(0);
              setAnswers({});
              setSaved(false);
            }}
            className="px-6 py-3 border border-border rounded-lg text-foreground hover:border-accent transition-colors"
          >
            Start Over
          </button>
        </div>
      </div>
    </div>
  );
}
